// Date rendering for records that mix two different KINDS of date.
//
// Salesforce Date fields (Order.EffectiveDate, Contract dates) come back as a
// bare calendar day: "2026-09-07". Salesforce DateTime fields, and everything
// out of Firestore or Stripe, come back as an instant: "2026-09-08T04:14:47Z".
//
// `new Date()` treats those two identically-looking strings very differently.
// A bare "2026-09-07" is parsed as UTC midnight, and the company runs on
// Arizona time (UTC-7, no DST), so rendering it in the local zone lands at
// 5pm on the 6th and the UI prints "Sep 6" for an order effective the 7th.
// Every order in the CRM was showing a day early — order 00000113, effective
// 2026-09-07, read "Sep 6, 2026" on the customer page.
//
// A calendar day has no time zone, so the fix is to never give it one: parse
// the parts and format them back in UTC. Instants keep their local-zone
// conversion, which for them is correct — that's what a timestamp is for.
//
// Sales.jsx had already worked this out and fixed it in its own local
// helper; Dashboard.jsx and CustomerDetail.jsx had not, and rendered the same
// Order.EffectiveDate a day early. Hence one shared function rather than a
// third copy.

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format a Salesforce/ISO date for display.
 *
 * @param {string|number|Date|null} value
 * @param {object} [opts]
 * @param {boolean} [opts.year=true]  include the year
 * @param {string}  [opts.fallback='—'] rendered when there is no value
 */
export function formatSfDate(value, { year = true, fallback = '—' } = {}) {
  if (!value) return fallback;

  const fmt = { month: 'short', day: 'numeric', ...(year ? { year: 'numeric' } : {}) };

  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    // Built in UTC and formatted in UTC: the calendar day survives whatever
    // zone the reader's browser is in.
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { ...fmt, timeZone: 'UTC' });
  }

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return fallback;
  return dt.toLocaleDateString('en-US', fmt);
}
